import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import getMetricSpec, {
  getFormatter,
  metricNameSelector,
  metricToolTipSelector,
  type MetricConfig,
  type MetricLabel,
  type MetricObject,
} from "../../utils/metrics";
import { MetricLabel as MetricLabelText } from "../../../../components/i18n/MetricLabel";
import KeywordCell from "../../../../components/custom/category/advertisements/KeywordCell";
import type { Column, SortFunction } from "react-table-6";
import type { ReactNode } from "react";
import { type FormatOptions } from "../../../../utils/formatNumber";

export interface MetricColumnOptions {
  editCell?: false;
  hideFilter?: boolean;
  filterType?: string;
  selectOptions?: { value: string; label: string }[];
  metric?: Partial<MetricObject>;
  compare?: Partial<MetricObject>;
  width?: number;
  overrideCompare?: unknown;
  overrideCompareValue?: unknown;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  overrideCompareFormatter?: (value: any) => ReactNode;
  columnTitle?: MetricLabel | string;
  checked?: boolean;
  maxWidth?: number;
  minWidth?: number;
  isStatic?: boolean;
  format?: MetricConfig["format"];
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => ReactNode;
  toolTip?: string;
  className?: string;
  overflowCell?: boolean;
  showBlank?: boolean;
  showDot?: boolean;
  overrideCompareAccessor?: (row: Record<string, unknown>) => unknown;
  isNegative?: boolean;
  noColor?: boolean;
  cellContainerClasses?: string;
  additionalStyles?: React.CSSProperties;
  height?: number;
  sortMethod?: SortFunction;
}

export type MetricColumn<T> = Column<T | undefined> &
  MetricConfig<T> & {
    name?: MetricLabel | string;
    toolTip?: MetricLabel | string;
    checked?: boolean;
    isStatic?: boolean;
    height?: number;
    selectOptions?: { value: string; label: string }[];
    filterType?: string;
    grouping?: string;
    filtered?: boolean;
    label?: string;
    clickable?: boolean;
    overrideValue?: number | null;
    formatter?: (value: unknown) => ReactNode;
    targetFormatter?: (value: unknown) => ReactNode;
    targetLabel?: string;
    compareFormatOptions?: FormatOptions;
    marketPlace?: string;
  };

const buildMetricColumn = <T,>(
  marketPlace: { marketPlace?: string },
  id: string,
  compareId?: string | null,
  options?: MetricColumnOptions,
  hideCompareValue?: boolean,
): MetricColumn<T> => {
  const { filterType, selectOptions = [] } = options ?? {};
  const metric = getMetricSpec(id);
  const mergedMetric = {
    ...metric,
    ...options?.metric,
  } as MetricObject<T>;
  const formatter = options?.formatter ?? getFormatter(marketPlace, mergedMetric.format, mergedMetric);
  mergedMetric.formatter = formatter;

  let compareSpec: MetricConfig<T> | null = null;
  if (compareId) {
    try {
      compareSpec = getMetricSpec(compareId);
    } catch (error) {
      // oxlint-disable-next-line no-console
      console.warn(`Compare metric with id "${compareId}" not found. Continuing without compare value.`, error);
    }
  }

  const mergedCompare: Partial<MetricObject<T>> = {
    ...compareSpec,
    ...options?.compare,
  };
  const compareFormatter = getFormatter(marketPlace, mergedCompare.format ?? "text", mergedCompare);
  mergedCompare.formatter = compareFormatter;

  // Below looks a bit funny, but allows overrides metric definitions to be shaped the same with all option params being processed
  if (options?.metric) {
    options = { ...options, ...(options.metric.options as Partial<MetricColumnOptions> | undefined) };
  }

  const accessor = mergedMetric.accessor;

  // Pending accept compare stats from sources outside of data report to match styles
  if (options?.overrideCompare && options.overrideCompareFormatter) {
    const { overrideCompareValue, overrideCompareFormatter } = options;
    mergedCompare.formatter = overrideCompareFormatter;
    mergedCompare.value = overrideCompareValue;
  }

  const defaultSortMethod = (a: unknown, b: unknown) => {
    if (a === null || a === undefined) {
      return mergedMetric.sortNullsLast ? 1 : -1;
    }
    if (b === null || b === undefined) {
      return mergedMetric.sortNullsLast ? -1 : 1;
    }
    return (
      a < b ? -1
      : a > b ? 1
      : 0
    );
  };

  const headerLabel: MetricLabel | string = options?.columnTitle ?? mergedMetric.name ?? metricNameSelector(id);
  return {
    Header: <MetricLabelText label={headerLabel} />,
    name: headerLabel,
    id,
    accessor,
    sortMethod:
      options?.sortMethod && typeof options.sortMethod === "function" ? options.sortMethod : defaultSortMethod,
    checked: options?.checked ?? true,
    maxWidth: options?.maxWidth,
    minWidth: options?.minWidth,
    isStatic: options?.isStatic,
    format: options?.format ? options.format : mergedMetric.format,
    width: options?.width,
    height: options?.height ?? 30,
    toolTip: options?.toolTip || metricToolTipSelector(id),
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    selectOptions: selectOptions,
    filterType: filterType,
    grouping: mergedMetric.grouping,
    Cell: (props) => {
      const value = accessor(props.row._original);
      if (props.row._original?.isStatsLoading && value === null) {
        return (
          <>
            <div
              style={{
                height: "1.3rem",
                maxWidth: "50px",
                marginLeft: "auto",
                marginRight: "5px",
                backgroundColor: "#ddd",
                borderRadius: "0.5rem",
              }}
            />
            {
              <div
                style={{
                  height: "1.3rem",
                  maxWidth: "40px",
                  marginTop: "0.5rem",
                  marginLeft: "auto",
                  marginRight: "5px",
                  backgroundColor: "#ddd",
                  borderRadius: "0.5rem",
                }}
              />
            }
          </>
        );
      }
      const fmt = options?.formatter || mergedMetric.formatter;

      if (options?.overflowCell) {
        return <KeywordCell value={fmt(value) as string} />;
      }
      if (options?.showBlank) {
        return (
          <div>
            {fmt(value)}
            <br />
            <span
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              -
            </span>
          </div>
        );
      }

      if ((options?.showDot ?? true) && !hideCompareValue) {
        const valueCmp =
          options?.overrideCompare ?
            typeof options.overrideCompareAccessor === "function" ?
              options.overrideCompareAccessor(props.row._original)
            : options.overrideCompare
          : (compareSpec?.accessor(props.row._original) ?? "");
        const fmtCmp = mergedCompare.formatter;
        const isNegative = options?.isNegative ?? mergedMetric.lower ?? false;
        const noColor = options?.noColor ?? false;
        let color = "#666";
        if (!noColor && (valueCmp as number) > 0) {
          color = isNegative ? trellisPalette[17] : trellisPalette[12];
        }
        if (!noColor && (valueCmp as number) < 0) {
          color = isNegative ? trellisPalette[12] : trellisPalette[17];
        }
        return (
          <div>
            {fmt(value)}
            <br />
            <span
              style={{
                color,
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              {fmtCmp?.(valueCmp)}
            </span>
          </div>
        );
      }
      return (
        <div
          className={
            options?.cellContainerClasses ?
              options.cellContainerClasses
            : `d-flex justify-content-end align-items-center h-100`
          }
        >
          <span>{fmt(value)}</span>
        </div>
      );
    },
    style: { height: "60px", ...options?.additionalStyles },
    filtered: true,
  };
};

export default buildMetricColumn;
